import { useEffect, useState } from 'react'
import SEO from '../components/SEO/SEO'
import TextInput from '../components/TextInput/TextInput'
import RadioInput from '../components/RadioInput/RadioInput'
import TextInputArea from '../components/TextInputArea/TextInputArea'
import FormSuccessDialog from '../components/FormSuccessDialog/FormSuccessDialog'
import FormErrorDialog from '../components/FormErrorDialog/FormErrorDialog'
import Layout from '../layout/Layout'
import CircularProgress from '@mui/material/CircularProgress'
import { z } from 'zod'

import classes from '../styles/section.module.css'
import useReportError from '../hooks/useReportError'
import { BASE_URL } from '../constants'
import { getRecaptchaV3Token } from '../utils/recaptcha'
import RecaptchaModal from '../components/Recaptcha/RecaptchaModal'
import useRecaptchaLoader from '../hooks/useRecaptchaLoader'

// Common validations
const phoneRegex = /^\d{10,20}$/
const zipRegex = /^\d{5}$/
const npiRegex = /^\d{10}$/
const tinRegex = /^\d{9}$/

// Helper function to transform empty strings to null
const emptyStringToNull = z
  .string()
  .transform(val => (val.trim() === '' ? null : val))

// Schema for Network Support form
const networkFormSchema = z
  .object({
    practice_group: z.string().min(1, 'Practice Group is required'),
    first_name: z.string().min(1, 'Provider First Name is required'),
    last_name: z.string().min(1, 'Provider Last Name is required'),
    specialty: z.string().min(1, 'Specialty is required'),
    date_of_birth: z.string().min(1, 'Date of Birth is required'),
    email_address: z.string().email('Invalid email address'),
    provider_npi: z
      .string()
      .regex(npiRegex, 'Provider NPI must be exactly 10 digits'),
    billing_tin: z.string().regex(tinRegex, 'Tax ID must be exactly 9 digits'),
    group_npi: z
      .string()
      .regex(npiRegex, 'Group NPI must be exactly 10 digits'),
    billing_address_1: z.string().min(1, 'Billing Address 1 is required'),
    billing_address_2: z.string().optional().nullable(),
    billing_city: z.string().min(1, 'Billing City is required'),
    billing_state: z.string().min(1, 'Billing State is required'),
    billing_zip: z
      .string()
      .regex(zipRegex, 'Billing ZIP must be exactly 5 digits'),
    billing_phone: z
      .string()
      .regex(phoneRegex, 'Billing phone must be between 10 and 20 digits'),
    fax: z
      .string()
      .regex(phoneRegex, 'Fax number must be between 10 and 20 digits'),
    affiliated_hospitals: z
      .string()
      .min(1, 'Hospital Affiliations is required'),
    servicing_address_1: z.string().min(1, 'Service Address 1 is required'),
    servicing_address_2: z.string().optional().nullable(),
    servicing_city: z.string().min(1, 'Service City is required'),
    servicing_state: z.string().min(1, 'Service State is required'),
    servicing_zip: z
      .string()
      .regex(zipRegex, 'Service ZIP must be exactly 5 digits'),
    license_no: z.string().min(1, 'License Number is required'),
    license_exp: z.string().min(1, 'License Expiration Date is required'),
    dea_no: z.string().optional().nullable(),
    effective_date: z.string().min(1, 'Effective Date is required'),
    additional_details: z.string().optional().nullable(),
  })
  .transform(data => {
    const optionalFields = [
      'dea_no',
      'billing_address_2',
      'servicing_address_2',
      'additional_details',
    ]
    const transformedData = Object.entries(data).reduce((acc, [key, value]) => {
      const isValueEmptyOrNull = value?.trim() === '' || value == null
      if (isValueEmptyOrNull) {
        acc[key] = optionalFields.includes(key) ? 'N/A' : null
      } else {
        acc[key] = value
      }
      return acc
    }, {})
    return transformedData
  })

const NetworkSupportForm = ({
  disabledField,
  setDisabledField,
  isSubmitting,
  setIsSubmitting,
  showError,
  setShowError,
  showSuccess,
  setShowSuccess,
  reportError,
}) => {
  const [formData, setFormData] = useState({
    practice_group: '',
    first_name: '',
    last_name: '',
    specialty: '',
    date_of_birth: '',
    provider_npi: '',
    billing_tin: '',
    group_npi: '',
    email_address: '',
    billing_address_1: '',
    billing_address_2: '',
    billing_city: '',
    billing_state: '',
    billing_zip: '',
    billing_phone: '',
    fax: '',
    affiliated_hospitals: '',
    servicing_address_1: '',
    servicing_address_2: '',
    servicing_city: '',
    servicing_state: '',
    servicing_zip: '',
    license_no: '',
    license_exp: '',
    dea_no: '',
    effective_date: '',
    additional_details: '',
  })

  const [validationErrors, setValidationErrors] = useState({})

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }))
  }

  const validateForm = () => {
    try {
      const result = networkFormSchema.safeParse(formData)
      if (result.success) {
        return { errors: {}, data: result.data }
      }
      const errors = result.error.errors.reduce((acc, curr) => {
        acc[curr.path[0]] = curr.message
        return acc
      }, {})
      return { errors, data: null }
    } catch (error) {
      console.error('Validation error:', error)
      return { errors: { form: 'An error occurred during validation. Please check your entries.' }, data: null }
    }
  }

  const handleSubmit = async e => {
    e.preventDefault()

    const { errors, data: parsedData } = validateForm()
    if (Object.keys(errors).length > 0 || !parsedData) {
      setValidationErrors(errors)
      return
    }

    setValidationErrors({})
    setDisabledField(true)
    setIsSubmitting(true)

    let token
    try {
  token = await getRecaptchaV3Token('request_to_join')
    } catch (err) {
      setShowError(true)
      setDisabledField(false)
      setIsSubmitting(false)
      return
    }

    const payload = {
      ...parsedData,
      captcha_token: {
        token,
        version: 'v3',
        provider: 1,
      },
    }

    let response = null
    try {
      response = await fetch(`${BASE_URL}/Marketing/RequestToJoin`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      })
    } catch (error) {
      setShowError(true)
      await reportError(error)
    }

    if (response && response.status === 400) {
      // server wants v2 verification
      setPendingSubmitData(payload)
      setShowCheckboxFallback(true)
      setDisabledField(false)
      setIsSubmitting(false)
      return
    }

    if (response && response?.status) {
      if (response.status < 200 || response.status > 299) {
        setShowError(true)
        await reportError(response)
      } else {
        setShowSuccess(true)
      }
    }
    setDisabledField(false)
    setIsSubmitting(false)
  }

  const [showCheckboxFallback, setShowCheckboxFallback] = useState(false)
  const [pendingSubmitData, setPendingSubmitData] = useState(null)

  const handleCheckboxVerify = async token => {
    if (!pendingSubmitData) return
    setShowCheckboxFallback(false)
    setDisabledField(true)
    setIsSubmitting(true)
    const response = await fetch(`${BASE_URL}/Marketing/RequestToJoin`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        ...pendingSubmitData,
        captcha_token: { token, version: 'v2', provider: 1 },
      }),
    })
    setPendingSubmitData(null)
    if (response.status >= 200 && response.status <= 299) {
      setShowSuccess(true)
    } else {
      setShowError(true)
    }
    setDisabledField(false)
    setIsSubmitting(false)
  }

  const renderInput = (field, label, options = {}) => (
    <div className="col-span-1 md:col-span-2">
      <TextInput
        {...options}
        disabled={disabledField}
        value={formData[field]}
        change={e => handleChange(field, e.target.value)}
        error={validationErrors[field]}
      >
        {label}
      </TextInput>
    </div>
  )

  return (
    <>
    <form
      onSubmit={handleSubmit}
      className="grid grid-cols-1 p-2 md:grid-cols-2 text-grey bg-grey-light"
    >
      {Object.keys(validationErrors).length > 0 && (
        <div className="col-span-1 md:col-span-2 mb-6">
          <div className="flex p-4 bg-[#ffe6e6] border-l-4 border-red-700">
            <div className="flex-shrink-0">
              <svg
                className="w-5 h-5 text-red-700"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-lg font-medium text-red-700">
                Please check your entries
              </h3>
              <div className="mt-2 text-sm text-red-700">
                <ul className="list-disc list-inside space-y-1">
                  {Object.values(validationErrors).map((error, index) => (
                    <li key={index}>{error}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="col-span-1 md:col-span-2">
        <TextInput
          required
          disabled={disabledField}
          value={formData.practice_group}
          change={e => handleChange('practice_group', e.target.value)}
          error={validationErrors.practice_group}
        >
          Practice Group
        </TextInput>
      </div>

      <div className="col-span-1">
        <TextInput
          required
          disabled={disabledField}
          value={formData.first_name}
          change={e => handleChange('first_name', e.target.value)}
          error={validationErrors.first_name}
        >
          Provider First Name
        </TextInput>
      </div>
      <div className="col-span-1">
        <TextInput
          required
          disabled={disabledField}
          value={formData.last_name}
          change={e => handleChange('last_name', e.target.value)}
          error={validationErrors.last_name}
        >
          Provider Last Name
        </TextInput>
      </div>

      <div className="col-span-1 md:col-span-2">
        <TextInput
          required
          disabled={disabledField}
          value={formData.email_address}
          change={e => handleChange('email_address', e.target.value)}
          error={validationErrors.email_address}
        >
          Email Address
        </TextInput>
      </div>

      <div className="col-span-1 md:col-span-2">
        <TextInput
          required
          disabled={disabledField}
          value={formData.specialty}
          change={e => handleChange('specialty', e.target.value)}
          error={validationErrors.specialty}
        >
          Specialty
        </TextInput>
      </div>

      <div className="col-span-1 md:col-span-2">
        <TextInput
          required
          type="date"
          disabled={disabledField}
          value={formData.date_of_birth}
          change={e => handleChange('date_of_birth', e.target.value)}
          error={validationErrors.date_of_birth}
        >
          Date of Birth
        </TextInput>
      </div>

      <div className="col-span-1 md:col-span-2">
        <TextInput
          required
          disabled={disabledField}
          value={formData.provider_npi}
          change={e => handleChange('provider_npi', e.target.value)}
          error={validationErrors.provider_npi}
        >
          Provider NPI
        </TextInput>
      </div>

      <div className="col-span-1 md:col-span-2">
        <TextInput
          required
          disabled={disabledField}
          value={formData.billing_tin}
          change={e => handleChange('billing_tin', e.target.value)}
          error={validationErrors.billing_tin}
        >
          Tax ID (TIN)
        </TextInput>
      </div>

      <div className="col-span-1 md:col-span-2">
        <TextInput
          required
          disabled={disabledField}
          value={formData.group_npi}
          change={e => handleChange('group_npi', e.target.value)}
          error={validationErrors.group_npi}
        >
          Group NPI
        </TextInput>
      </div>

      <div className="col-span-1 md:col-span-2">
        <TextInput
          required
          disabled={disabledField}
          value={formData.billing_address_1}
          change={e => handleChange('billing_address_1', e.target.value)}
          error={validationErrors.billing_address_1}
        >
          Billing Address 1
        </TextInput>
      </div>

      <div className="col-span-1 md:col-span-2">
        <TextInput
          disabled={disabledField}
          value={formData.billing_address_2}
          change={e => handleChange('billing_address_2', e.target.value)}
          error={validationErrors.billing_address_2}
        >
          Billing Address 2
        </TextInput>
      </div>

      <div className="col-span-1 md:col-span-2">
        <TextInput
          required
          disabled={disabledField}
          value={formData.billing_city}
          change={e => handleChange('billing_city', e.target.value)}
          error={validationErrors.billing_city}
        >
          Billing City
        </TextInput>
      </div>

      <div className="col-span-1 md:col-span-2">
        <TextInput
          required
          disabled={disabledField}
          value={formData.billing_state}
          change={e => handleChange('billing_state', e.target.value)}
          error={validationErrors.billing_state}
        >
          Billing State
        </TextInput>
      </div>

      <div className="col-span-1 md:col-span-2">
        <TextInput
          required
          disabled={disabledField}
          value={formData.billing_zip}
          change={e => handleChange('billing_zip', e.target.value)}
          error={validationErrors.billing_zip}
        >
          Billing ZIP
        </TextInput>
      </div>

      <div className="col-span-1 md:col-span-2">
        <TextInput
          required
          disabled={disabledField}
          value={formData.billing_phone}
          change={e => handleChange('billing_phone', e.target.value)}
          error={validationErrors.billing_phone}
        >
          Billing Phone
        </TextInput>
      </div>

      <div className="col-span-1 md:col-span-2">
        <TextInput
          required
          disabled={disabledField}
          value={formData.fax}
          change={e => handleChange('fax', e.target.value)}
          error={validationErrors.fax}
        >
          Fax #
        </TextInput>
      </div>

      <div className="col-span-1 md:col-span-2">
        <TextInput
          required
          disabled={disabledField}
          value={formData.affiliated_hospitals}
          change={e => handleChange('affiliated_hospitals', e.target.value)}
          error={validationErrors.affiliated_hospitals}
        >
          Hospital Affiliations
        </TextInput>
      </div>

      <div className="col-span-1 md:col-span-2">
        <TextInput
          required
          disabled={disabledField}
          value={formData.servicing_address_1}
          change={e => handleChange('servicing_address_1', e.target.value)}
          error={validationErrors.servicing_address_1}
        >
          Service Address 1
        </TextInput>
      </div>

      <div className="col-span-1 md:col-span-2">
        <TextInput
          disabled={disabledField}
          value={formData.servicing_address_2}
          change={e => handleChange('servicing_address_2', e.target.value)}
          error={validationErrors.servicing_address_2}
        >
          Service Address 2
        </TextInput>
      </div>

      <div className="col-span-1 md:col-span-2">
        <TextInput
          required
          disabled={disabledField}
          value={formData.servicing_city}
          change={e => handleChange('servicing_city', e.target.value)}
          error={validationErrors.servicing_city}
        >
          Service City
        </TextInput>
      </div>

      <div className="col-span-1 md:col-span-2">
        <TextInput
          required
          disabled={disabledField}
          value={formData.servicing_state}
          change={e => handleChange('servicing_state', e.target.value)}
          error={validationErrors.servicing_state}
        >
          Service State
        </TextInput>
      </div>

      <div className="col-span-1 md:col-span-2">
        <TextInput
          required
          disabled={disabledField}
          value={formData.servicing_zip}
          change={e => handleChange('servicing_zip', e.target.value)}
          error={validationErrors.servicing_zip}
        >
          Service ZIP
        </TextInput>
      </div>

      <div className="col-span-1 md:col-span-2">
        <TextInput
          required
          disabled={disabledField}
          value={formData.license_no}
          change={e => handleChange('license_no', e.target.value)}
          error={validationErrors.license_no}
        >
          License #
        </TextInput>
      </div>

      <div className="col-span-1 md:col-span-2">
        <TextInput
          required
          type="date"
          disabled={disabledField}
          value={formData.license_exp}
          change={e => handleChange('license_exp', e.target.value)}
          error={validationErrors.license_exp}
        >
          License Expiration Date
        </TextInput>
      </div>

      <div className="col-span-1 md:col-span-2">
        <TextInput
          disabled={disabledField}
          value={formData.dea_no}
          change={e => handleChange('dea_no', e.target.value)}
          error={validationErrors.dea_no}
        >
          DEA # (Optional)
        </TextInput>
      </div>

      <div className="col-span-1 md:col-span-2">
        <TextInput
          required
          type="date"
          disabled={disabledField}
          value={formData.effective_date}
          change={e => handleChange('effective_date', e.target.value)}
          error={validationErrors.effective_date}
          title="The date you would like to start seeing Evry Health members"
        >
          Effective Date
        </TextInput>
      </div>

      <div className="col-span-1 md:col-span-2">
        <TextInputArea
          disabled={disabledField}
          value={formData.additional_details}
          change={e => handleChange('additional_details', e.target.value)}
          error={validationErrors.additional_details}
        >
          Additional Details
        </TextInputArea>
      </div>

      <div className="col-span-1 mx-auto my-8 md:col-span-2">
        <button
          type="submit"
          disabled={isSubmitting}
          className="flex items-center gap-2 px-12 py-2 text-lg font-medium text-white bg-blue disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSubmitting && <CircularProgress size="1.5rem" />}
          Send
        </button>
      </div>
  </form>
  {showCheckboxFallback && (
      <div style={{ position: 'fixed', inset: 0, zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ background: 'rgba(0,0,0,0.5)', position: 'absolute', inset: 0 }} />
        <div style={{ zIndex: 10000, background: '#fff', padding: 20, borderRadius: 8 }}>
          <p style={{ marginBottom: 10 }}>Please complete the verification to continue</p>
          <RecaptchaModal onVerify={token => handleCheckboxVerify(token)} onError={() => { setShowCheckboxFallback(false); setShowError(true) }} onClose={() => setShowCheckboxFallback(false)} />
          <div style={{ marginTop: 8, textAlign: 'right' }}>
            <button type="button" className="px-4 py-2 text-sm" onClick={() => { setShowCheckboxFallback(false) }}>
              Cancel
            </button>
          </div>
        </div>
      </div>
    )}
    </>
  )
}

const JoinTheEvryNetwork = () => {
  useRecaptchaLoader()
  const [disabledField, setDisabledField] = useState(false)
  const [showError, setShowError] = useState(false)
  const [showSuccess, setShowSuccess] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const { reportError } = useReportError()

  const toggleModal = () => {
    setShowError(false)
    setShowSuccess(false)
  }

  return (
    <>
      <SEO />
      <Layout>
        <div className="relative">
          <div className="absolute inset-x-0 bottom-0 bg-gray-100 h-1/2" />
          <div className="mx-auto">
            <div className="relative">
              <div className="absolute inset-0">
                <img
                  className="object-cover w-full h-full"
                  src="banner/person-on-phone.png"
                  alt="Person on the phone"
                />
              </div>
              <div className="relative px-4 py-16 sm:px-6 md:py-32 md:px-80">
                <h1 className="text-3xl tracking-tight md:text-5xl text-blue-dark">
                  Join the{' '}
                  <span className="block text-globeGreen">Evry Network</span>
                </h1>
              </div>
            </div>
          </div>
        </div>

        <section>
          <div className="max-w-screen-xl px-12 py-12 mx-auto">
            {showSuccess && <FormSuccessDialog toggleModal={toggleModal} />}

            {showError && (
              <FormErrorDialog
                click={e => {
                  setShowError(false)
                }}
              />
            )}
            <div className="grid md:grid-cols-2 md:gap-16">
              <div>
                <div className="col-span-1">
                  <h2 className="pb-10 text-3xl tracking-tight md:text-4xl text-darkBlue">
                    Evry&#39;s different in every way.
                    <span className="block text-blue">
                      We&#39;re excited to know you.
                    </span>
                  </h2>
                  <ol
                    className={`${classes.chekmarkList} text-lg text-grey font-[sans-serif]`}
                  >
                    <li>
                      <p>
                        We pride ourselves on delivering world-class customer
                        service and make every interaction simple. Our provider
                        services are just a phone call away.
                      </p>
                    </li>
                    <li>
                      <p>
                        We built one-of-a-kind technology to receive, process
                        and pay claims accurately and on time.
                      </p>
                    </li>
                    <li>
                      <p>
                        We pay claims faster - think hours and days, not weeks.
                      </p>
                    </li>
                    <li>
                      <p>
                        No games. No headaches. Just an amazing experience from
                        start to finish.
                      </p>
                    </li>
                    <li>
                      <p>
                        Our members love us because working with Evry is simple,
                        painless and transparent.
                      </p>
                    </li>
                    <li>
                      <p>
                        We have built advanced systems designed to enable your
                        practice to thrive under new reimbursement models.
                      </p>
                    </li>
                    <li>
                      <p>Many additional income opportunities!</p>
                      <div className="pl-6">
                        <ul className="list-disc ">
                          <li>
                            <p>Telehealth</p>
                          </li>
                          <li>
                            <p>Clinical Teaming</p>
                          </li>
                          <li>
                            <p>
                              Ingesting medical notes for your patients from
                              other providers
                            </p>
                          </li>
                          <li>
                            <p>
                              Bonuses for referrals into specialized wellness
                              programs
                            </p>
                          </li>
                        </ul>
                      </div>
                    </li>
                  </ol>
                </div>
              </div>
              <div>
                <div>
                  <h3 className="pb-4 text-3xl font-bold uppercase text-darkBlue">
                    Request To Join
                  </h3>
                  <p className="pb-6 text-sm text-grey">
                    Roster updates can also be sent to{' '}
                    <a
                      href="mailto:networksupport@evryhealth.com"
                      className="text-green hover:underline"
                    >
                      networksupport@evryhealth.com
                    </a>
                    .
                  </p>
                  <NetworkSupportForm
                    disabledField={disabledField}
                    setDisabledField={setDisabledField}
                    isSubmitting={isSubmitting}
                    setIsSubmitting={setIsSubmitting}
                    showError={showError}
                    setShowError={setShowError}
                    showSuccess={showSuccess}
                    setShowSuccess={setShowSuccess}
                    reportError={reportError}
                  />
                </div>
              </div>
            </div>
          </div>
        </section>
      </Layout>
    </>
  )
}

export default JoinTheEvryNetwork
